import { FC, useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'

import { App, Button, Form, Input, Tabs, Typography } from 'antd'

import { useLoginMutation, useRegisterMutation } from '../../graphql'
import { Context as AuthContext } from '../../providers/Auth'

type RegisterFormValues = {
  email: string
  username: string
  password: string
}

type LoginFormValues = {
  username: string
  password: string
}

const RegistrationForm: FC<{ onSuccess: VoidFunction }> = ({ onSuccess }) => {
  const { formatMessage } = useIntl()
  const { notification } = App.useApp()
  const [register] = useRegisterMutation()
  const { setUser, setToken } = useContext(AuthContext)
  const onFormFinish = useCallback(
    (input: RegisterFormValues) => {
      register({ variables: { input } })
        .then((response) => {
          if (response.data?.register) {
            notification.success({
              message: formatMessage({ id: 'register.success.title' }),
              description: formatMessage({ id: 'register.success.description' }),
            })
            localStorage.setItem('token', response.data?.register?.jwt || '')
            setToken(response.data?.register?.jwt || '')
            setUser(response.data?.register?.user)
            onSuccess()
          } else {
            notification.error({
              message: formatMessage({ id: 'register.error.title' }),
              description: formatMessage({ id: 'register.error.description' }),
            })
          }
        })
        .catch(() =>
          notification.error({
            message: formatMessage({ id: 'register.error.title' }),
            description: formatMessage({ id: 'register.error.description' }),
          }),
        )
    },
    [register, notification, formatMessage, onSuccess, setUser, setToken],
  )
  const [form] = Form.useForm()

  return (
    <Form<RegisterFormValues> form={form} layout={'vertical'} onFinish={onFormFinish}>
      <Form.Item
        label={formatMessage({ id: 'register.form.username' })}
        name={'username'}
        rules={[{ required: true, message: formatMessage({ id: 'form.required-field-error' }) }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={formatMessage({ id: 'register.form.email' })}
        name={'email'}
        rules={[{ required: true, message: formatMessage({ id: 'form.required-field-error' }) }]}
      >
        <Input type={'email'} />
      </Form.Item>
      <Form.Item
        label={formatMessage({ id: 'register.form.password' })}
        name={'password'}
        rules={[{ required: true, message: formatMessage({ id: 'form.required-field-error' }) }]}
      >
        <Input type={'password'} />
      </Form.Item>
      <Button htmlType={'submit'} type={'primary'} block>
        {formatMessage({ id: 'register.form.button' })}
      </Button>
    </Form>
  )
}

const LoginForm: FC<{ onSuccess: VoidFunction }> = ({ onSuccess }) => {
  const { formatMessage } = useIntl()
  const [form] = Form.useForm()
  const [login] = useLoginMutation()
  const { notification } = App.useApp()
  const { setUser, setToken } = useContext(AuthContext)
  const onFormFinish = useCallback(
    (input: LoginFormValues) => {
      login({ variables: { input: { identifier: input.username, password: input.password } } })
        .then((response) => {
          if (response.data?.login) {
            notification.success({
              message: formatMessage({ id: 'login.success.title' }),
              description: formatMessage({ id: 'login.success.description' }),
            })
            localStorage.setItem('token', response.data?.login?.jwt || '')
            setToken(response.data?.login?.jwt || '')
            setUser(response.data?.login?.user)
            onSuccess()
          } else {
            notification.error({
              message: formatMessage({ id: 'login.error.title' }),
              description: formatMessage({ id: 'login.error.description' }),
            })
          }
        })
        .catch(() => {
          notification.error({
            message: formatMessage({ id: 'login.error.title' }),
            description: formatMessage({ id: 'login.error.description' }),
          })
        })
      onSuccess()
    },
    [onSuccess, login, notification, formatMessage, setToken, setUser],
  )

  return (
    <Form<LoginFormValues> form={form} layout={'vertical'} onFinish={onFormFinish}>
      <Form.Item
        name={'username'}
        label={formatMessage({ id: 'login.form.username' })}
        rules={[{ required: true, message: formatMessage({ id: 'form.required-field-error' }) }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label={formatMessage({ id: 'login.form.password' })}
        name={'password'}
        rules={[{ required: true, message: formatMessage({ id: 'form.required-field-error' }) }]}
      >
        <Input type={'password'} />
      </Form.Item>
      <Button htmlType={'submit'} type={'primary'} block>
        {formatMessage({ id: 'login.form.button' })}
      </Button>
    </Form>
  )
}

const Auth: FC<{ onSuccess: VoidFunction }> = ({ onSuccess }) => {
  const { formatMessage } = useIntl()

  return (
    <Tabs
      centered
      items={[
        {
          label: (
            <Typography.Text style={{ width: 144, display: 'block', textAlign: 'center' }}>
              {formatMessage({ id: 'auth.form.register' })}
            </Typography.Text>
          ),
          key: 'register',
          children: <RegistrationForm onSuccess={onSuccess} />,
        },
        {
          label: (
            <Typography.Text style={{ width: 144, display: 'block', textAlign: 'center' }}>
              {formatMessage({ id: 'auth.form.login' })}
            </Typography.Text>
          ),
          key: 'login',
          children: <LoginForm onSuccess={onSuccess} />,
        },
      ]}
    />
  )
}

export { Auth as default }
